import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, startOfWeek, startOfMonth, subDays, subWeeks, subMonths, format, eachHourOfInterval, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';

export type TimeFilter = 'day' | 'week' | 'month';

export interface AdminDashboardStats {
  revenue: {
    current: number;
    previous: number;
    average: number;
    percentChange: number;
    percentVsAverage: number;
  };
  ordersDelivered: {
    current: number;
    previous: number;
    average: number;
    percentChange: number;
    percentVsAverage: number;
  };
  ordersPosted: {
    current: number;
    previous: number;
    average: number;
    percentChange: number;
    percentVsAverage: number;
  };
  ordersInProgress: {
    current: number;
    previous: number;
    average: number;
    percentChange: number;
    percentVsAverage: number;
  };
  chartData: Array<{
    date: string;
    revenue: number;
    orders: number;
  }>;
}

const getDateRanges = (filter: TimeFilter) => {
  const now = new Date();
  let currentStart: Date, previousStart: Date, previousEnd: Date;

  switch (filter) {
    case 'day':
      currentStart = startOfDay(now);
      previousStart = startOfDay(subDays(now, 1));
      previousEnd = now;
      break;
    case 'week':
      currentStart = startOfWeek(now, { weekStartsOn: 1 });
      previousStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      previousEnd = startOfWeek(now, { weekStartsOn: 1 });
      break;
    case 'month':
      currentStart = startOfMonth(now);
      previousStart = startOfMonth(subMonths(now, 1));
      previousEnd = startOfMonth(now);
      break;
  }

  return { currentStart, previousStart, previousEnd, now };
};

const calculatePercentChange = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

const formatChartLabel = (date: Date, filter: TimeFilter): string => {
  switch (filter) {
    case 'day':
      return format(date, 'HH:mm');
    case 'week':
      return format(date, 'EEE', { locale: fr });
    case 'month':
      return format(date, 'dd/MM');
  }
};

const initializeChartData = (currentStart: Date, now: Date, filter: TimeFilter) => {
  const dataMap = new Map<string, { date: string; revenue: number; orders: number }>();
  
  if (filter === 'day') {
    // Pour un jour : une entrée par heure
    const hours = eachHourOfInterval({ start: currentStart, end: now });
    hours.forEach(hour => {
      const label = formatChartLabel(hour, filter);
      dataMap.set(label, { date: label, revenue: 0, orders: 0 });
    });
  } else {
    // Pour semaine/mois : une entrée par jour
    const days = eachDayOfInterval({ start: currentStart, end: now });
    days.forEach(day => {
      const label = formatChartLabel(day, filter);
      dataMap.set(label, { date: label, revenue: 0, orders: 0 });
    });
  }
  
  return dataMap;
};

export const useAdminDashboardStats = (filter: TimeFilter = 'month') => {
  return useQuery({
    queryKey: ['admin-dashboard-stats', filter],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { currentStart, previousStart, previousEnd, now } = getDateRanges(filter);

      // Revenue current period
      const { data: currentRevenue } = await supabase
        .from('orders')
        .select('final_price')
        .eq('status', 'delivered')
        .gte('updated_at', currentStart.toISOString())
        .lte('updated_at', now.toISOString());

      const currentRevenueValue = currentRevenue?.reduce((sum, order) => sum + (Number(order.final_price) || 0), 0) || 0;

      // Revenue previous period
      const { data: previousRevenue } = await supabase
        .from('orders')
        .select('final_price')
        .eq('status', 'delivered')
        .gte('updated_at', previousStart.toISOString())
        .lte('updated_at', previousEnd.toISOString());

      const previousRevenueValue = previousRevenue?.reduce((sum, order) => sum + (Number(order.final_price) || 0), 0) || 0;

      // Average revenue (last 6 months)
      const { data: historicalRevenue } = await supabase
        .from('orders')
        .select('final_price')
        .eq('status', 'delivered')
        .gte('updated_at', subMonths(now, 6).toISOString());

      const averageRevenue = (historicalRevenue?.reduce((sum, order) => sum + (Number(order.final_price) || 0), 0) || 0) / 6;

      // Orders delivered
      const { count: deliveredCurrent } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'delivered')
        .gte('updated_at', currentStart.toISOString())
        .lte('updated_at', now.toISOString());

      const { count: deliveredPrevious } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'delivered')
        .gte('updated_at', previousStart.toISOString())
        .lte('updated_at', previousEnd.toISOString());

      const { count: deliveredHistorical } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'delivered')
        .gte('updated_at', subMonths(now, 6).toISOString());

      const averageDelivered = (deliveredHistorical || 0) / 6;

      // Orders posted
      const { count: postedCurrent } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'posted')
        .gte('created_at', currentStart.toISOString())
        .lte('created_at', now.toISOString());

      const { count: postedPrevious } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'posted')
        .gte('created_at', previousStart.toISOString())
        .lte('created_at', previousEnd.toISOString());

      const { count: postedHistorical } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'posted')
        .gte('created_at', subMonths(now, 6).toISOString());

      const averagePosted = (postedHistorical || 0) / 6;

      // Orders in progress
      const { count: inProgressCurrent } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .in('status', ['booked', 'assigned', 'in_transit'])
        .gte('updated_at', currentStart.toISOString())
        .lte('updated_at', now.toISOString());

      const { count: inProgressPrevious } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .in('status', ['booked', 'assigned', 'in_transit'])
        .gte('updated_at', previousStart.toISOString())
        .lte('updated_at', previousEnd.toISOString());

      const { count: inProgressHistorical } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .in('status', ['booked', 'assigned', 'in_transit'])
        .gte('updated_at', subMonths(now, 6).toISOString());

      const averageInProgress = (inProgressHistorical || 0) / 6;

      // Chart data (période courante selon le filtre)
      const { data: chartOrders } = await supabase
        .from('orders')
        .select('created_at, final_price, status')
        .gte('created_at', currentStart.toISOString())
        .lte('created_at', now.toISOString())
        .order('created_at', { ascending: true });

      // Initialiser les données du graphique avec toutes les périodes (heures ou jours)
      const chartDataMap = initializeChartData(currentStart, now, filter);

      // Remplir avec les vraies données
      chartOrders?.forEach(order => {
        const orderDate = new Date(order.created_at);
        const label = formatChartLabel(orderDate, filter);
        
        const existing = chartDataMap.get(label);
        if (existing) {
          existing.orders += 1;
          if (order.status === 'delivered' && order.final_price) {
            existing.revenue += Number(order.final_price);
          }
        }
      });

      const chartData = Array.from(chartDataMap.values());

      return {
        revenue: {
          current: currentRevenueValue,
          previous: previousRevenueValue,
          average: averageRevenue,
          percentChange: calculatePercentChange(currentRevenueValue, previousRevenueValue),
          percentVsAverage: calculatePercentChange(currentRevenueValue, averageRevenue),
        },
        ordersDelivered: {
          current: deliveredCurrent || 0,
          previous: deliveredPrevious || 0,
          average: averageDelivered,
          percentChange: calculatePercentChange(deliveredCurrent || 0, deliveredPrevious || 0),
          percentVsAverage: calculatePercentChange(deliveredCurrent || 0, averageDelivered),
        },
        ordersPosted: {
          current: postedCurrent || 0,
          previous: postedPrevious || 0,
          average: averagePosted,
          percentChange: calculatePercentChange(postedCurrent || 0, postedPrevious || 0),
          percentVsAverage: calculatePercentChange(postedCurrent || 0, averagePosted),
        },
        ordersInProgress: {
          current: inProgressCurrent || 0,
          previous: inProgressPrevious || 0,
          average: averageInProgress,
          percentChange: calculatePercentChange(inProgressCurrent || 0, inProgressPrevious || 0),
          percentVsAverage: calculatePercentChange(inProgressCurrent || 0, averageInProgress),
        },
        chartData,
      } as AdminDashboardStats;
    },
  });
};
